import {
  Box,
  Button,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import * as React from 'react';
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useContextSelector } from 'use-context-selector';
import { serverUrl } from '../config';
import { colors, styled } from '../Stitches';
import EditorContext from './Editor/EditorContext';
import Emoji from './Emoji';
import { Fetching } from './Fetching';
import { useFilter } from './hooks/useFilter';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';

interface LetterSummary {
  sender_first_name: string;
  sender_last_name: string;
  deliver_at: string;
  id: string;
  title: string;
}

const fetchLetters =
  (token: string) => (letterId: string) => async (): Promise<LetterSummary[]> => {
    const fetchUrl = `${serverUrl}/api/letters/received-summary`;
    const request = {
      method: 'post',
      headers: { authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ letterId }),
    };

    return await fetch(fetchUrl, request).then((res) => res.json());
  };

type LetterSummaryLineProps = {
  summary: LetterSummary;
  onClick: (id: string) => void;
};

const LetterSummaryLine = ({
  summary: { id, title, sender_first_name, sender_last_name, deliver_at },
  onClick,
}: LetterSummaryLineProps) => {
  const delivered = format(new Date(deliver_at), 'eo MMMM yyyy');

  const background = useColorModeValue('purple.50', 'purple.500');

  return (
    <Stack _hover={{ background: background }} p={2} onClick={(_) => onClick(id)}>
      <Stack direction="row" justifyContent="space-between">
        <Text fontSize="sm">
          {sender_first_name} {sender_last_name}
        </Text>
        <Text fontSize="sm">{delivered}</Text>
      </Stack>
      <Text>{title}</Text>
    </Stack>
  );
};

type LetterSummaryProps = {
  setId: (id: string) => void;
  letterId: string;
  filter: string | undefined;
  onClose: () => void;
};

const LetterSummaryList = ({ setId, filter, onClose, letterId }: LetterSummaryProps) => {
  const authedFetchLetters = useAuthenticateApiCall(fetchLetters);
  const { data, isLoading } = useQuery(['letter-summary'], authedFetchLetters(letterId));

  const filtered = useFilter(
    (l: LetterSummary) => [`${l.sender_first_name} ${l.sender_last_name}`, l.title],
    data,
    filter
  );

  return (
    <Box p={isLoading ? 2 : 0}>
      <Fetching skeletonType="List" isFetching={isLoading}>
        {data?.length === 0 ? (
          <Box p={2}>
            <Text>You haven{"'"}t received and letters yet.</Text>
          </Box>
        ) : (
          <Stack spacing={2}>
            {filtered &&
              filtered.map((summary) => (
                <LetterSummaryLine
                  key={summary.id}
                  summary={summary}
                  onClick={(id) => {
                    setId(id);
                    onClose();
                  }}
                />
              ))}
          </Stack>
        )}
      </Fetching>
    </Box>
  );
};

type PickerButtonProps = { sideCarOpen: boolean; onClose: () => void };

const PickerButton = ({ sideCarOpen, onClose }: PickerButtonProps) => (
  <Stack direction="row">
    {sideCarOpen && (
      <Button aria-label="Close second column" onClick={onClose}>
        <Emoji symbol="←" label="Emoji of a left arrow" />
      </Button>
    )}

    <Tooltip
      placement="top"
      label={!sideCarOpen ? 'Open a received letter in sidecar' : 'Switch sidecar letter'}
    >
      {/* This div is required because Tooltip and PopoverTrigger can't share a trigger element. */}
      <div>
        <PopoverTrigger>
          <Button
            aria-label={
              sideCarOpen ? 'Change letter in side car' : 'Open received letter in side car'
            }
          >
            <Emoji symbol="📄" spaceRight label="Emoji of an open letter" />
            {sideCarOpen ? (
              <Emoji symbol="↓" label="Emoji of a down arrow" />
            ) : (
              <Emoji symbol="→" label="Emoji of a right arrow" />
            )}
          </Button>
        </PopoverTrigger>
      </div>
    </Tooltip>
  </Stack>
);

type LetterPickerProps = {
  setSideCarLetter: (id: string | undefined) => void;
  sideCarOpen: boolean;
  letterId: string;
};

const LetterPickerInner = ({ setSideCarLetter, sideCarOpen, letterId }: LetterPickerProps) => {
  const focusRef = useRef(null);

  const [search, setSearch] = useState<string | undefined>(undefined);

  const popoverBackground = useColorModeValue(undefined, colors.dark.whiteAlpha100);

  return (
    <Popover initialFocusRef={focusRef} placement="bottom-end">
      {({ onClose }) => (
        <>
          <PickerButton sideCarOpen={sideCarOpen} onClose={() => setSideCarLetter(undefined)} />
          <PopoverContent maxW="xl" backgroundColor={popoverBackground}>
            <PopoverArrow backgroundColor={popoverBackground} />
            <PopoverHeader>
              <Input
                ref={focusRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
              />
            </PopoverHeader>
            <PopoverBody p={0} maxH="512" overflowY="scroll">
              <LetterSummaryList
                filter={search}
                letterId={letterId}
                setId={setSideCarLetter}
                onClose={onClose}
              />
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

const ShowHide = styled('div', {
  variants: {
    show: {
      true: { display: 'inherit' },
      false: { display: 'none' },
    },
  },
});

export const LetterPicker = () => {
  const id = useContextSelector(EditorContext, (ctx) => ctx.id);
  const dispatch = useContextSelector(EditorContext, (ctx) => ctx.dispatch);
  const { letterId } = useContextSelector(EditorContext, (ctx) => ctx.sideCar);

  const setSideCarLetter = React.useCallback(
    (id: string | undefined) => {
      if (id) {
        dispatch({ type: 'SideCar', event: { type: 'Open', id } });
      } else {
        dispatch({ type: 'SideCar', event: { type: 'Close' } });
      }
    },
    [dispatch]
  );

  return (
    <ShowHide show={{ '@initial': false, '@md': true }}>
      <LetterPickerInner
        setSideCarLetter={setSideCarLetter}
        sideCarOpen={!!letterId}
        letterId={id}
      />
    </ShowHide>
  );
};
